// Angular
import { Injectable, inject } from '@angular/core';

// Third party
import { select, Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

// Local library
import { TError } from '@amwager/util-ts';

// Local file
import * as AuthActions from './auth.actions';
import * as AuthSelectors from './auth.selectors';
import { ILogin } from './auth.models';



@Injectable()
export class AuthFacade {
  private readonly store = inject(Store);

  // Selectors — expose store state as observables

  isError$(): Observable<TError> {
    return this.store.pipe(select(AuthSelectors.getAuthError));
  }

  // login
  login$(): Observable<ILogin | undefined> {
    return this.store.pipe(select(AuthSelectors.getLogin));
  }

  isLoggingIn$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsLoggingIn));
  }

  // isLoginSuccess$(): Observable< boolean | undefined > {
  //   return this.store.pipe(select(AuthSelectors.getIsLoginSuccess));
  // }

  isLoginSuccessResponse$(): Observable< boolean | undefined > {
    return this.store.pipe(select(AuthSelectors.getIsLoginSuccessResponse));
  }

  isLoginSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccessLogin: boolean | undefined
  }> {
    return this.store.pipe(select(AuthSelectors.getIsLoginSuccess));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // isLoginFailed$(): Observable<any | undefined> {
  //   return this.store.pipe(select(AuthSelectors.getIsLoginFailed));
  // }

  // logout
  isLoggingOut$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsLoggingOut));
  }

  isLogoutSuccess$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsLogoutSuccess));
  }

  //forgot password
  isRenewingPassword$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsRenewingPassword));
  }

  isRenewPasswordSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccessRenewPassword: boolean | undefined
  }> {
    return this.store.pipe(select(AuthSelectors.getIsRenewPasswordSuccess));
  }

  // update password
  isUpdatingPassword$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsUpdatingPassword));
  }

  isUpdatePasswordSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccessUpdatePassword: boolean | undefined
  }> {
    return this.store.pipe(select(AuthSelectors.getIsUpdatePasswordSuccess));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // isUpdatePasswordFailed$(): Observable<any | undefined> {
  //   return this.store.pipe(select(AuthSelectors.getIsUpdatePasswordFailed));
  // }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // isRenewPasswordFailed$(): Observable<any | undefined> {
  //   return this.store.pipe(select(AuthSelectors.getIsRenewPasswordFailed));
  // }

  // remember me
  isRememberMe$(): Observable<boolean | undefined> {
    return this.store.select(AuthSelectors.getIsRememberMe);
  }

  // register
  isRegistering$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsRegistering));
  }

  isRegisterSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccessRegister: boolean | undefined
  }> {
    return this.store.pipe(select(AuthSelectors.getIsRegisterSuccess));
  }

   // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // isRegisterFailed$(): Observable<any | undefined> {
  //   return this.store.pipe(select(AuthSelectors.getIsRegisterFailed));
  // }

  // complete registration
  isCompleteRegistering$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsCompleteRegistering));
  }

  isCompleteRegistrationSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccessCompleteRegistration: boolean | undefined
  }> {
    return this.store.pipe(select(AuthSelectors.getIsCompleteRegistrationSuccess));
  }

  // documents verification
  isDocumentsVerificating$(): Observable<boolean | undefined> {
    return this.store.pipe(select(AuthSelectors.getIsDocumentsVerificating));
  }

  isDocumentsVerificationSuccess$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    isSuccessDocumentsVerification: boolean | undefined
  }> {
    return this.store.pipe(select(AuthSelectors.getIsDocumentsVerificationSuccess));
  }

  // user fullname
  getUserFullName$(): Observable<string | undefined> {
    return this.store.select(AuthSelectors.getUserFullName);
  }

  // user id documents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIdFile$(): Observable<any> {
    return this.store.select(AuthSelectors.getIdFile);
  }

  // user bill documents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getBillFile$(): Observable<any> {
    return this.store.select(AuthSelectors.getBillFIle);
  }

  /**
   * Country States
   * using response as param
   *
   */
  getCountryStates$(): Observable<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
  }> {
    return this.store.select(AuthSelectors.getCountryStates);
  }

  /**
   * Country States
   *
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // getCountryStates$(): Observable<any> {
  //   return this.store.pipe(select(AuthSelectors.getCountryStates));
  // }

  /**
   * Use the initialization action to perform one
   * or more tasks in your Effects.
   *
   * Dispatches: Requests only, from AuthActions
   *
   */


  /**
   * Initialize the auth state (idempotent — do not call multiple times)
   * @param {string} sourceInitiator Component that triggered init
   * @returns {void}
   */
  initState(sourceInitiator: string): void {
    this.store.select(AuthSelectors.getAuthLoaded)
      .pipe(take(1))
      .subscribe(isLoaded => {
        isLoaded
          ? null
          : this.store.dispatch(AuthActions.initAuthState({ sourceInitiator }))
      });
  }
  clearState(sourceInitiator: string): void {
    this.store.dispatch(AuthActions.clearAuthState({ sourceInitiator }));
  }

  setError(error: TError): void {
    this.store.dispatch(AuthActions.setError({ error }));
  }

  // register
  register(username: string, password: string, tmsId: string, talon: string, fingerprint: string): void {
    this.store.dispatch(AuthActions.registerAPI({ username, password, tmsId, talon, fingerprint }));
  }

  // complete registration
  completeRegistration(
    firstName: string,
    lastName: string,
    phoneNumber: string,
    dateOfBirth: string,
    streetAddress: string,
    city: string,
    state: string,
    zipCode: string,
    ssn: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tmsId: string | undefined,
    talon: string
  ): void {
    this.store.dispatch(AuthActions.completeRegistrationAPI({
      firstName,
      lastName,
      phoneNumber,
      dateOfBirth,
      streetAddress,
      city,
      state,
      zipCode,
      ssn,
      tmsId,
      talon
    }));
  }

  // documents verification
  documentsVerification(
    customerId: string | undefined, idDocuments: string, billDocuments: string
  ): void {
    this.store.dispatch(AuthActions.uploadDocumentsAPI({ customerId, idDocuments, billDocuments }));
  }

  // country states
  jurisdictions(country: string): void {
    this.store.dispatch(AuthActions.jurisdictionsAPI({ country }));
  }

  // update password
  updatePassword(newPassword: string, replaceIdentifier: string): void {
    this.store.dispatch(AuthActions.updatePasswordAPI({ newPassword, replaceIdentifier }));
  }

  // login
  login(username: string, password: string, tmsId: string, talon: string, from?: string): void {
    this.store.dispatch(AuthActions.loggingInAPI({ username, password, tmsId, from, talon }));
  }

  loginGuest(): void {
    this.store.dispatch(AuthActions.loggingGuestAPI());
  }

  setIsAuthenticated(isAuthenticated: boolean): void {
    this.store.dispatch(AuthActions.setIsAuthenticated({ isAuthenticated }));
  }

  setIsRememberMe(isRememberMe: boolean): void {
    return this.store.dispatch(AuthActions.setIsRememberMe({ isRememberMe }))
  }

  // logout
  logout(from?: string): void {
    this.store.dispatch(AuthActions.loggingOutAPI({ from }));
  }

  // forgot password
  renewPassword(email: string): void {
    this.store.dispatch(AuthActions.renewPasswordAPI({ email }));
  }

  // user fullname
  setUserFullName(isFullName: string): void {
    return this.store.dispatch(AuthActions.setUserFullName({ isFullName }));
  }

  // user id documents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setIdFile(idFile: any): void {
    return this.store.dispatch(AuthActions.setIdFile({ idFile }));
  }

  // user bill documents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setBillFile(billFile: any): void {
    return this.store.dispatch(AuthActions.setBillFile({ billFile }));
  }

  // country states
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setCountryStates(subdivisions: any): void {
    return this.store.dispatch(AuthActions.setCountryStates({ subdivisions }));
  }

}
